import React from "react";
import type { Lang } from "../../../../api/serving";
import { chassis } from "./chassis";
import { peT, type PeLessonStrings } from "../../../../i18n/peLesson";

interface Props {
  value: Lang;
  onChange: (lang: Lang) => void;
  /**
   * The language the assignment was launched with (the EXISTING per-student /
   * per-assignment setting). Shown as a hint so a toggle away from it is
   * visible — the toggle reflects this setting, it does not replace it.
   */
  assignmentDefault: Lang;
  /** Run E2 J5: string table of the screen (defaults to the shown language). */
  t?: PeLessonStrings;
}

const OPTIONS: Lang[] = ["en", "es"];
const SHORT: Record<Lang, string> = { en: "EN", es: "ES" };

/**
 * EN/ES display toggle. Initialized by the caller from the launch `lang` param
 * (the per-assignment language setting); toggling changes only what is rendered.
 */
const LanguageToggle: React.FC<Props> = ({ value, onChange, assignmentDefault, t = peT(value) }) => {
  return (
    <div className="inline-flex items-center gap-2">
      <div
        className="inline-flex rounded-md border overflow-hidden"
        style={{ borderColor: chassis.accent }}
        role="group"
        aria-label={t.displayLanguage}
      >
        {OPTIONS.map((opt) => {
          const active = opt === value;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              aria-pressed={active}
              className="px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
              style={{
                backgroundColor: active ? chassis.accent : "transparent",
                color: active ? chassis.accentText : chassis.accent,
              }}
            >
              {SHORT[opt]}
            </button>
          );
        })}
      </div>
      {value !== assignmentDefault && (
        <span className="text-xs" style={{ color: chassis.textSecondary }}>
          {t.assigned}: {SHORT[assignmentDefault]}
        </span>
      )}
    </div>
  );
};

export default LanguageToggle;
